/**
 * Feb 2026 · Admin Settings — Company Profile editor.
 *
 * Backs the print-header helper: `print.js` reads whatever is saved here
 * (via GET /api/company-profile at app boot). Editing gated by the
 * `masters:edit` permission so only admins can change GSTIN / addresses.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { setPrintCompany } from "@/config/company";
import { BuildingsIcon, FloppyDiskIcon, ImageIcon, FileTextIcon, PlusIcon, PencilSimpleIcon, TrashIcon, CheckCircleIcon, StarIcon, HashIcon } from "@phosphor-icons/react";
import InvoiceNumberingPanel from "./InvoiceNumberingPanel";

const inp = "w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const area = "w-full min-h-[80px] px-2.5 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y";

const EMPTY = {
  name: "", tagline: "", logo: "", logoWidth: 44,
  address: "", phone: "", email: "", website: "",
  gstin: "", pan: "", cin: "", iec: "", udyam: "",
  // Feb 2026 · GST-invoice footer bits — State + State Code, banking &
  // legal jurisdiction. All optional; blank values render as "—" on print.
  state: "", state_code: "",
  bank_name: "", bank_account_no: "", bank_ifsc: "", bank_branch: "",
  bank_swift: "", bank_iban: "", jurisdiction_city: "",
};

export default function AdminSettings() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Aug 2026 · Tab switcher — Company Profile vs Invoice Numbering.
  const [tab, setTab] = useState("company");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company-profile");
        setForm({ ...EMPTY, ...(data || {}) });
      } catch (e) { /* first-boot has no doc; keep defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Feb 2026 · Company logo now lives in Object Storage. The profile doc
  // carries a relative `/api/files/company/<uuid>.<ext>` URL; resolve it
  // against REACT_APP_BACKEND_URL so <img src> works in both the
  // dashboard preview (same origin, relative works) and the standalone
  // preview after upload (before form.logo is committed).
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolveLogo = (v) => {
    if (!v) return "";
    if (v.startsWith("data:") || v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return `${backendUrl}${v}`;
    return v;
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo too large — max 5MB"); return; }
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(f.type)) { toast.error("PNG / JPG / SVG / WEBP only"); return; }
    // Feb 2026 · Push to Object Storage via multipart upload — the server
    // compresses with Pillow and stores under `mes-narrowfabric/company/`,
    // then stamps a `/api/files/company/<uuid>.<ext>` URL back onto the
    // profile doc so `company_profiles` stays tiny and CDN-friendly.
    const fd = new FormData();
    fd.append("file", f, f.name);
    try {
      const { data } = await api.post("/company-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(x => ({ ...x, ...(data || {}) }));
      setPrintCompany(data || form);
      toast.success("Logo uploaded to Object Storage");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      e.target.value = "";  // let the user pick the same file again if needed
    }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Company Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/company-profile", { ...form, logoWidth: Number(form.logoWidth) || 44 });
      // Hot-swap the in-memory cache so the NEXT print picks up the change
      // without a page refresh.
      setPrintCompany(data || form);
      toast.success("Company Profile saved · applied to next print");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-zinc-500">Loading Company Profile…</div>;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`h-10 px-4 rounded-sm text-xs font-mono uppercase tracking-widest flex items-center gap-2 border transition-colors ${tab === id ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white hover:border-[var(--accent)]/60"}`}
    >
      <Icon size={14} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Aug 2026 · Tab bar */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] pb-3">
        <TabBtn id="company" icon={BuildingsIcon} label="Company Profile" />
        <TabBtn id="numbering" icon={HashIcon} label="Invoice Numbering" />
      </div>

      {tab === "numbering" && <InvoiceNumberingPanel />}

      {tab === "company" && (<>
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-display text-3xl font-bold text-white">Company Profile</h1>
          <div className="text-xs text-zinc-500 mt-1">These details print on every Sales Order, Delivery Challan, Tax Invoice and Packing List.</div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="company-save"
          className="h-10 px-4 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          <FloppyDiskIcon size={14} weight="bold" /> {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Identity */}
        <div className="tactical-card p-5 lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2"><BuildingsIcon size={16} className="text-[var(--accent)]" /><div className="overline">Identity</div></div>
          <F label="Company Name *"><input data-testid="company-name" value={form.name} onChange={set("name")} className={inp} /></F>
          <F label="Tagline"><input data-testid="company-tagline" value={form.tagline} onChange={set("tagline")} className={inp} placeholder="e.g. Elastic Weaving · Woven / Knitted / Jacquard" /></F>
          <F label="Registered Address (multi-line)"><textarea data-testid="company-address" value={form.address} onChange={set("address")} className={area} placeholder="Street / City / State / Pincode" /></F>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <F label="Phone"><input data-testid="company-phone" value={form.phone} onChange={set("phone")} className={inp} /></F>
            <F label="Email"><input data-testid="company-email" value={form.email} onChange={set("email")} className={inp} /></F>
            <F label="Website"><input data-testid="company-website" value={form.website} onChange={set("website")} className={inp} /></F>
          </div>
        </div>

        {/* Logo */}
        <div className="tactical-card p-5 space-y-3">
          <div className="flex items-center gap-2"><ImageIcon size={16} className="text-[var(--accent)]" /><div className="overline">Logo</div></div>
          {form.logo ? (
            <div className="border border-[var(--border)] rounded-sm p-4 flex items-center justify-center bg-[var(--bg-0)]">
              <img src={resolveLogo(form.logo)} alt="logo preview" data-testid="company-logo-preview" className="max-h-24 object-contain" />
            </div>
          ) : (
            <div className="border-2 border-dashed border-[var(--border)] rounded-sm p-6 text-center text-xs text-zinc-500">No logo uploaded</div>
          )}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoFile} data-testid="company-logo-file" className="text-xs" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Width (mm)"><input type="number" min="20" max="80" value={form.logoWidth} onChange={set("logoWidth")} className={inp} data-testid="company-logo-width" /></F>
            <F label="&nbsp;">
              <button onClick={() => setForm(x => ({ ...x, logo: "" }))} className="h-9 w-full rounded-sm border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10" data-testid="company-logo-clear">Clear logo</button>
            </F>
          </div>
        </div>

        {/* Tax IDs */}
        <div className="tactical-card p-5 lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          <div className="col-span-full flex items-center gap-2"><div className="overline">Tax & Regulatory IDs</div><div className="text-[10px] text-zinc-500">These appear on every invoice for GST compliance.</div></div>
          <F label="GSTIN"><input data-testid="company-gstin" value={form.gstin} onChange={(e) => setForm(x => ({ ...x, gstin: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="PAN"><input data-testid="company-pan" value={form.pan} onChange={(e) => setForm(x => ({ ...x, pan: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="CIN"><input data-testid="company-cin" value={form.cin} onChange={(e) => setForm(x => ({ ...x, cin: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="IEC"><input data-testid="company-iec" value={form.iec} onChange={set("iec")} className={`${inp} font-mono`} /></F>
          <F label="Udyam"><input data-testid="company-udyam" value={form.udyam} onChange={(e) => setForm(x => ({ ...x, udyam: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="State"><input data-testid="company-state" value={form.state} onChange={set("state")} placeholder="e.g. Tamil Nadu" className={inp} /></F>
          <F label="State Code"><input data-testid="company-state-code" value={form.state_code} onChange={(e) => setForm(x => ({ ...x, state_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))} placeholder="e.g. 33" className={`${inp} font-mono`} /></F>
          <F label="Jurisdiction City"><input data-testid="company-jurisdiction" value={form.jurisdiction_city} onChange={set("jurisdiction_city")} placeholder="e.g. Coimbatore" className={inp} /></F>
        </div>

        {/* Feb 2026 · Banking block — printed on every issued invoice
            footer so buyers can remit funds without a second email. */}
        <div className="tactical-card p-5 lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          <div className="col-span-full flex items-center gap-2"><div className="overline">Bank Details</div><div className="text-[10px] text-zinc-500">Appears in the "Bank Details" box on every invoice / proforma PDF.</div></div>
          <F label="Bank Name"><input data-testid="company-bank-name" value={form.bank_name} onChange={set("bank_name")} className={inp} /></F>
          <F label="Branch"><input data-testid="company-bank-branch" value={form.bank_branch} onChange={set("bank_branch")} className={inp} /></F>
          <F label="A/C No"><input data-testid="company-bank-account" value={form.bank_account_no} onChange={set("bank_account_no")} className={`${inp} font-mono`} /></F>
          <F label="IFSC"><input data-testid="company-bank-ifsc" value={form.bank_ifsc} onChange={(e) => setForm(x => ({ ...x, bank_ifsc: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="SWIFT (for Exports)"><input data-testid="company-bank-swift" value={form.bank_swift} onChange={(e) => setForm(x => ({ ...x, bank_swift: e.target.value.toUpperCase() }))} className={`${inp} font-mono`} /></F>
          <F label="IBAN (Foreign Remittance)"><input data-testid="company-bank-iban" value={form.bank_iban} onChange={(e) => setForm(x => ({ ...x, bank_iban: e.target.value.toUpperCase().replace(/\s+/g, '') }))} placeholder="e.g. IN65HDFC0000123456789012" className={`${inp} font-mono`} /></F>
        </div>

        {/* Feb 2026 · Terms & Conditions Master — list / create / edit /
            delete / mark-default templates that appear in the Invoice + PI
            editor's T&C picker. */}
        <div className="tactical-card p-5 lg:col-span-3">
          <TncMaster />
        </div>
      </div>
      </>)}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="overline mb-1.5" dangerouslySetInnerHTML={{ __html: label }} />
      {children}
    </div>
  );
}

// -----------------------------------------------------------------
// Feb 2026 · Terms & Conditions Master
//   Lists all T&C templates, allows create / edit / delete, and lets
//   the admin flag exactly one template as default. The default flows
//   automatically into every new GST Invoice / Proforma Invoice via
//   the backend (see routes/gst_invoices.py + proforma_invoices.py).
// -----------------------------------------------------------------
function TncMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | row (edit)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tnc-templates/");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load T&C templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSetDefault = async (row) => {
    try {
      await api.post(`/tnc-templates/${row.id}/set-default`);
      toast.success(`"${row.name}" is now the default template`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to set default"); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tnc-templates/${row.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="tnc-master">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileTextIcon size={16} className="text-[var(--accent)]" />
          <div className="overline">Terms &amp; Conditions Templates</div>
          <div className="text-[10px] text-zinc-500 hidden sm:block">
            Reusable T&amp;C blocks for Invoices &amp; Proforma Invoices. Mark one as default to auto-apply.
          </div>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="tnc-add-btn"
          className="h-9 px-3 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-1.5"
        >
          <PlusIcon size={12} weight="bold" /> New Template
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Loading templates…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] rounded-sm p-6 text-center text-xs text-zinc-500" data-testid="tnc-empty">
          No T&amp;C templates yet. Create the first one to get started.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--border)] rounded-sm">
          <table className="w-full text-xs" data-testid="tnc-table">
            <thead className="bg-[var(--bg-0)] text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Preview</th>
                <th className="px-3 py-2 text-right w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[var(--border)] hover:bg-white/[0.02]" data-testid={`tnc-row-${r.id}`}>
                  <td className="px-3 py-2">
                    {r.is_default ? (
                      <span title="Default template" className="inline-flex" data-testid={`tnc-default-badge-${r.id}`}>
                        <StarIcon size={14} weight="fill" className="text-amber-400" />
                      </span>
                    ) : (
                      <button
                        title="Mark as default"
                        onClick={() => onSetDefault(r)}
                        data-testid={`tnc-set-default-${r.id}`}
                        className="text-zinc-600 hover:text-amber-400"
                      >
                        <StarIcon size={14} />
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 font-semibold text-white">
                    {r.name}
                    {r.is_default && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 text-[9px] uppercase tracking-widest">
                        <CheckCircleIcon size={9} weight="fill" /> Default
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 max-w-xl truncate">
                    {(r.text || "").split("\n")[0].slice(0, 120)}{(r.text || "").length > 120 ? "…" : ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`tnc-edit-${r.id}`}
                        className="h-7 px-2 rounded-sm border border-[var(--border)] hover:border-[var(--accent)] text-xs flex items-center gap-1"
                      >
                        <PencilSimpleIcon size={11} /> Edit
                      </button>
                      <button
                        onClick={() => onDelete(r)}
                        data-testid={`tnc-delete-${r.id}`}
                        className="h-7 px-2 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs flex items-center gap-1"
                      >
                        <TrashIcon size={11} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <TncEditor
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TncEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name || "");
  const [text, setText] = useState(initial?.text || "");
  const [isDefault, setIsDefault] = useState(!!initial?.is_default);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = name.trim(), t = text.trim();
    if (!n || !t) { toast.error("Name and text are required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/tnc-templates/", { name: n, text: t, is_default: isDefault });
        toast.success("Template created");
      } else {
        await api.put(`/tnc-templates/${initial.id}`, { name: n, text: t, is_default: isDefault });
        toast.success("Template updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="tnc-editor">
      <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="font-display text-lg font-semibold text-white">
            {isNew ? "New T&C Template" : `Edit — ${initial.name}`}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Template Name *">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Domestic Standard, Export FOB"
              className={inp}
              data-testid="tnc-editor-name"
              autoFocus
            />
          </F>
          <F label="Terms & Conditions Text *">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={10}
              placeholder={"1. Payment due within 30 days from invoice date.\n2. Interest @ 18% p.a. on overdue balances.\n3. Goods once sold cannot be returned…"}
              className={`${area} min-h-[220px] font-mono`}
              data-testid="tnc-editor-text"
            />
          </F>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              data-testid="tnc-editor-default"
            />
            <StarIcon size={12} className="text-amber-400" />
            Set as default template (auto-applied to new Invoices &amp; Proforma Invoices)
          </label>
        </div>
        <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-3 rounded-sm border border-[var(--border)] text-xs hover:bg-white/[0.03]"
            data-testid="tnc-editor-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="tnc-editor-save"
            className="h-9 px-4 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            <FloppyDiskIcon size={12} weight="bold" /> {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
